/**
 * A client's RCS: what it is charged per billing event — the platform's card,
 * or its own, which is never under the platform's minimums — and what RCS took
 * from its one wallet. Messages are charged as they are delivered, by the API;
 * the wallet is topped up under Wallet & credits.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessagesSquare } from 'lucide-react';
import {
  ApiError,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  QueryPending,
  ReadOnly,
  Select,
  StatusChip,
  api,
  formatDateTime,
  toast,
  useCan,
} from '@connect/web-shared';

import { formatMetered } from './ai-card';
import { ServiceSpend } from './service-spend';
import { PRICE, RATE_KEYS, belowMinimum, rcsKeys, type RcsClient, type RcsRates } from './rcs-wallet';

const KIND_LABELS = { top_up: 'Top-up', charge: 'RCS', refund: 'Refund', adjustment: 'Adjustment' };

export function RcsWalletCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.rcs.edit');
  const url = `/messaging/rcs/clients/${clientPublicId}`;
  const key = rcsKeys.client(clientPublicId);
  const rcs = useQuery({ queryKey: key, queryFn: () => api.get<RcsClient>(url) });

  const update = useMutation({
    mutationFn: (body: { pricing?: RcsRates | null }) =>
      api.patch<RcsClient>(url, body),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      toast.success('Saved');
    },
    onError: (e) => toast.error('Could not change it', e instanceof ApiError ? e.message : undefined),
  });

  if (rcs.data === undefined) return <QueryPending query={rcs} label="Loading the RCS wallet" />;
  const data = rcs.data;
  const wallet = data.wallet;
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <MessagesSquare className="size-4" />
            RCS
          </span>
        }
        description={`${clientName}'s RCS is paid from its wallet. Each message is charged when it is delivered, at the prices below, before GST.`}
      />
      <CardBody className="space-y-3">
        <ServiceSpend
          balance={wallet.balance}
          currency={wallet.currency}
          low={wallet.low}
          spent={wallet.spent_30_days}
        />

        <RatesEditor
          key={JSON.stringify(data.own_pricing)}
          data={data}
          canEdit={canEdit}
          saving={update.isPending}
          onSave={(pricing) => update.mutate({ pricing })}
        />

        <p className="text-xs font-medium text-muted">RCS's latest movements</p>
        {wallet.entries.length === 0 ? (
          <p className="text-sm text-muted">No movements yet.</p>
        ) : (
          <table className="w-full text-xs">
            <tbody>
              {wallet.entries.slice(0, 10).map((entry) => (
                <tr key={entry.public_id} className="border-t border-line">
                  <td className="py-1.5 text-muted">{formatDateTime(entry.at)}</td>
                  <td className="py-1.5">
                    <StatusChip
                      value={entry.kind}
                      labels={KIND_LABELS}
                      tones={{ top_up: 'success', refund: 'success', charge: 'neutral', adjustment: 'info' }}
                    />
                  </td>
                  <td className="max-w-xs truncate py-1.5 text-muted">{entry.description}</td>
                  <td className="py-1.5 text-right font-mono tabular">{formatMetered(entry.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

/** The client's prices: the platform's, or its own — each at least its minimum. */
function RatesEditor({
  data,
  canEdit,
  saving,
  onSave,
}: {
  data: RcsClient;
  canEdit: boolean;
  saving: boolean;
  onSave: (pricing: RcsRates | null) => void;
}) {
  const [own, setOwn] = useState(data.own_pricing !== null);
  const [rates, setRates] = useState<RcsRates>(data.own_pricing ?? data.wallet.rates);
  const labels = Object.fromEntries(data.wallet.rate_labels.map((l) => [l.key, l.label]));
  const under = own ? belowMinimum(rates, data.minimums) : [];
  const malformed = own && RATE_KEYS.some((k) => !PRICE.test(rates[k]));
  const changed =
    own !== (data.own_pricing !== null) || (own && JSON.stringify(rates) !== JSON.stringify(data.own_pricing));
  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-2 rounded border border-line p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-medium text-muted">Price per billing event (₹, before GST)</p>
          <Select
            size="sm"
            containerClassName="w-64"
            aria-label="Prices"
            value={own ? 'own' : 'platform'}
            options={[
              { value: 'platform', label: "The platform's prices" },
              { value: 'own', label: 'Its own prices' },
            ]}
            onChange={(e) => {
              const next = e.target.value === 'own';
              setOwn(next);
              if (next && !data.own_pricing) setRates(data.wallet.rates);
            }}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {RATE_KEYS.map((key) => (
            <Input
              key={key}
              size="sm"
              label={labels[key] ?? key}
              inputMode="decimal" disabled={!own}
              value={own ? rates[key] : data.wallet.rates[key]}
              hint={`At least ₹${data.minimums[key]}`}
              error={under.includes(key) ? `Under the minimum of ₹${data.minimums[key]}` : undefined}
              onChange={(e) => setRates({ ...rates, [key]: e.target.value.trim() })}
            />
          ))}
        </div>
        {canEdit && changed ? (
          <div className="flex justify-end">
            <Button size="sm" loading={saving} disabled={under.length > 0 || malformed} onClick={() => onSave(own ? rates : null)}>
              Save the prices
            </Button>
          </div>
        ) : null}
      </div>
    </ReadOnly>
  );
}
